import React from "react";
import { useTranslation } from "react-i18next";
import { Icon, IconName } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";
import { useListType } from "./useListType";

export type ListType = "grid" | "list" | "kanban";

export interface ViewOption {
  value: ListType;
  icon: IconName;
  labelKey: string;
}

export interface ListTypeSwitchProps {
  defaultListType?: ListType;
  variant?: "compact" | "default";
  options?: ListType[];
  className?: string;
}

const viewOptions: ViewOption[] = [
  { value: "kanban", icon: "Kanban", labelKey: "common.kanban_view" },
  { value: "grid", icon: "Grid", labelKey: "common.grid_view" },
  { value: "list", icon: "List", labelKey: "common.list_view" },
];

const ListTypeSwitch: React.FC<ListTypeSwitchProps> = ({
  defaultListType = "grid",
  variant = "default",
  options = ["grid", "list"],
  className,
}) => {
  const { t } = useTranslation();
  const { view, setListType } = useListType(defaultListType);

  const listOptions = viewOptions.filter((option) =>
    options.includes(option.value)
  );

  if (variant === "compact") {
    return (
      <div className={cn("flex items-center space-x-2 h-fit", className)}>
        {listOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setListType(option.value)}
            className={cn(
              "p-2 rounded-md transition-colors duration-200",
              view === option.value
                ? "bg-gray-100 text-gray-900"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            )}
            aria-label={t(option.labelKey)}
          >
            <Icon
              icon={option.icon}
              size="sm"
              color={view === option.value ? "black" : "grey"}
            />
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative inline-flex transition-all duration-300 ease-in-out h-fit",
        className
      )}
    >
      <div
        className={cn(
          "absolute top-0 bottom-0 bg-gray-100 border border-gray-200 rounded-full left-0 right-0"
        )}
      />
      {listOptions.map((option, index) => (
        <button
          key={option.value}
          onClick={() => setListType(option.value)}
          className={cn(
            "relative flex items-center space-x-2 px-4 py-1 rounded-full transition-all duration-300 ease-in-out z-10",
            view === option.value
              ? "text-gray-900 border border-gray-300 bg-white"
              : "text-gray-500 border border-transparent hover:text-gray-700 "
          )}
        >
          <Icon
            icon={option.icon}
            size="sm"
            color={view === option.value ? "black" : "grey"}
          />
          <span className="text-sm font-medium whitespace-nowrap">
            {t(option.labelKey)}
          </span>
        </button>
      ))}
    </div>
  );
};

export { ListTypeSwitch };
